<template>
  <HrPageLayout
    title="Submit Unused Leave"
    subtitle="Employee Self-service · My Leave"
    header-tone="institutional"
    :breadcrumb-items="[
      { label: 'ESS', route: '/employee-dashboard' },
      { label: 'My Leave', route: '/employee-leave-history' },
      { label: 'Submit Unused Leave' }
    ]"
  >
    <div class="grid">
      <section class="card">
        <h2 class="h2">New claim</h2>
        <p class="muted">
          Submit a formal request to have unused leave days credited back to your balance after dual approval (Director HR, then Welfare).
        </p>
        <form class="form" @submit.prevent="submitClaim">
          <label class="lbl">Employee</label>
          <input class="inp" type="text" :value="employeeLabel" disabled />

          <label class="lbl">Leave type <span class="req">*</span></label>
          <select v-model.number="form.leaveTypeID" class="inp" required>
            <option :value="0" disabled>Select leave type</option>
            <option v-for="t in leaveTypes" :key="t.id" :value="Number(t.id)">
              {{ t.leaveTypeName }} — entitlement up to {{ entitlementFor(t.id) }} days
            </option>
          </select>

          <label class="lbl">Number of days <span class="req">*</span></label>
          <input v-model.number="form.daysClaimed" class="inp" type="number" min="1" step="1" required />

          <label class="lbl">Remarks / justification <span class="req">*</span></label>
          <textarea v-model="form.remarks" class="inp ta" rows="4" required placeholder="Explain the basis for your unused leave claim"></textarea>

          <label class="lbl">Supporting document (optional)</label>
          <input ref="fileInput" type="file" class="inp" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />

          <div class="actions">
            <button type="submit" class="btn primary" :disabled="saving">{{ saving ? "Submitting…" : "Submit claim" }}</button>
            <button type="button" class="btn ghost" @click="loadMine" :disabled="loading">Refresh list</button>
          </div>
        </form>
      </section>

      <section class="card">
        <h2 class="h2">My submissions</h2>
        <div v-if="loading" class="muted">Loading…</div>
        <div v-else-if="!claims.length" class="muted">No claims yet.</div>
        <div v-else class="table-wrap">
          <table class="tbl">
            <thead>
              <tr>
                <th>Type</th>
                <th>Days</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in claims" :key="c.id">
                <td>{{ c.leaveTypeName }}</td>
                <td>{{ c.daysClaimed }}</td>
                <td><span class="pill" :class="statusClass(c.status)">{{ c.status }}</span></td>
                <td>{{ c.updatedAt }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "UnusedLeaveSubmit",
  components: { HrPageLayout },
  data() {
    return {
      leaveTypes: [],
      balances: [],
      claims: [],
      loading: false,
      saving: false,
      employeeLabel: "",
      form: {
        leaveTypeID: 0,
        daysClaimed: 1,
        remarks: "",
        year: new Date().getFullYear()
      }
    };
  },
  mounted() {
    this.initEmployee();
    this.loadTypes();
    this.loadBalances();
    this.loadMine();
  },
  methods: {
    initEmployee() {
      try {
        const raw = sessionStorage.getItem("userInfo");
        const u = raw ? JSON.parse(raw) : {};
        // loginPortal stores names + idNo (not nationalId / firstName / lastName)
        const id = String(
          u.nationalId || u.NationalId || u.idNo || u.IdNo || sessionStorage.getItem("sessionIdNo") || ""
        ).trim();
        const fromParts = [u.firstName, u.middleName, u.lastName].filter(Boolean).join(" ").trim();
        const nm = fromParts || String(u.names || "").trim();
        if (nm && id) this.employeeLabel = `${nm} (${id})`;
        else if (nm) this.employeeLabel = nm;
        else if (id) this.employeeLabel = id;
        else this.employeeLabel = "—";
      } catch {
        this.employeeLabel = "—";
      }
    },
    async loadTypes() {
      try {
        const fd = new FormData();
        fd.append("function", "getLeaveTypes");
        const res = await execute(fd, authUrl);
        if (res.data.success) this.leaveTypes = res.data.data || [];
      } catch (_) {
        this.leaveTypes = [];
      }
    },
    async loadBalances() {
      try {
        const fd = new FormData();
        fd.append("function", "getEmployeeLeaveBalance");
        const res = await execute(fd, authUrl);
        if (res.data.success) this.balances = res.data.data || [];
      } catch (_) {
        this.balances = [];
      }
    },
    entitlementFor(leaveTypeId) {
      const b = this.balances.find((x) => String(x.leaveTypeID) === String(leaveTypeId));
      if (!b) return "—";
      const t = Number(b.totalDays || 0) + Number(b.carriedOverDays || 0);
      return Number.isFinite(t) ? t : "—";
    },
    async loadMine() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "listUnusedLeaveClaims");
        fd.append("view", "mine");
        const res = await execute(fd, authUrl);
        if (res.data.success) this.claims = res.data.data?.claims || [];
        else this.claims = [];
      } catch (_) {
        this.claims = [];
      } finally {
        this.loading = false;
      }
    },
    statusClass(s) {
      const x = String(s || "").toLowerCase();
      if (x === "approved") return "ok";
      if (x === "rejected") return "bad";
      return "pend";
    },
    async submitClaim() {
      if (!this.form.leaveTypeID || this.form.daysClaimed < 1 || String(this.form.remarks).trim().length < 5) {
        alert("Complete all required fields (remarks at least 5 characters).");
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "submitUnusedLeaveClaim");
        fd.append("leaveTypeID", String(this.form.leaveTypeID));
        fd.append("daysClaimed", String(Math.floor(this.form.daysClaimed)));
        fd.append("remarks", String(this.form.remarks).trim());
        fd.append("year", String(this.form.year));
        const f = this.$refs.fileInput?.files?.[0];
        if (f) fd.append("attachment", f);
        const res = await execute(fd, authUrl);
        if (!res.data.success) {
          alert(res.data.message || "Submit failed");
          return;
        }
        alert("Claim submitted.");
        this.form.remarks = "";
        this.form.daysClaimed = 1;
        this.form.leaveTypeID = 0;
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        await this.loadMine();
      } catch (e) {
        alert(e?.response?.data?.message || e.message || "Error");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
@media (max-width: 1024px) {
  .grid {
    grid-template-columns: 1fr;
  }
}
.card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
}
.h2 {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
}
.muted {
  color: #64748b;
  font-size: 13px;
  margin: 0 0 16px 0;
}
.form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.lbl {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.req {
  color: #b91c1c;
}
.inp {
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
}
.inp:disabled {
  background: #f8fafc;
  color: #64748b;
}
.ta {
  resize: vertical;
  min-height: 100px;
}
.actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}
.btn {
  border-radius: 10px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}
.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.primary {
  background: linear-gradient(180deg, #1e4976, #0c2d48);
  color: #fff;
}
.ghost {
  background: #fff;
  border-color: #cbd5e1;
  color: #334155;
}
.table-wrap {
  overflow: auto;
  max-height: 420px;
}
.tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.tbl th,
.tbl td {
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 6px;
  text-align: left;
}
.pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.pill.ok {
  background: #dcfce7;
  color: #166534;
}
.pill.bad {
  background: #fee2e2;
  color: #991b1b;
}
.pill.pend {
  background: #e0f2fe;
  color: #075985;
}
</style>
